import { BehaviorSubject, Observable } from 'rxjs';
import { Page } from '../paginator/page.type';
import { PagedQuery } from '../../query/paged-query';
import { signal, Signal } from '@angular/core';

export interface DataSource<T> {
    sortBy(field: string, direction: 'asc' | 'desc' | undefined): DataSource<T>;
    clearSorting(): DataSource<T>;
    fetch(): void;
    goToPage(page: number): void;
    /**
     * Altera a quantidade de itens por página e recarrega a partir da 1ª
     * página. Fontes sem paginação real (ex.: {@link ArrayDataSource}) tratam
     * como no-op.
     *
     * @param pageSize Novo tamanho de página.
     */
    changePageSize(pageSize: number): void;
    onReady(): Observable<Page<T>>;
    isLoading(): Observable<boolean>;
}

export class ArrayDataSource<T> implements DataSource<T> {
    private pagedData: BehaviorSubject<Page<T>>;
    private pagedData$: Observable<Page<T>>;
    /**
     * Estado de carregamento da fonte. Fontes em array carregam os dados por
     * fora (ex.: uma chamada HTTP no componente), então expõem esse sinal para
     * que o datatable mostre o **mesmo overlay/spinner** das fontes paginadas.
     */
    private readonly loadingSubject = new BehaviorSubject<boolean>(false);
    /** Dados na ordem original (a ordenação é aplicada sobre esta base). */
    private originalData: T[];
    /** Campo e direção de ordenação atuais (client-side). */
    private sortField: string | null = null;
    private sortDirection: 'asc' | 'desc' | undefined = undefined;

    constructor(data: T[]) {
        this.originalData = data;
        this.pagedData = new BehaviorSubject<Page<T>>(this.buildPage(data));
        this.pagedData$ = this.pagedData.asObservable();
    }

    public setData(data: T[]): void {
        this.originalData = data;
        this.emit();
        // Dados recebidos: encerra o carregamento.
        this.loadingSubject.next(false);
    }

    /**
     * Marca/desmarca o carregamento da fonte. Chame `setLoading(true)` antes de
     * iniciar a carga dos dados; `setData` encerra automaticamente.
     *
     * @param loading `true` enquanto os dados estão sendo carregados.
     */
    public setLoading(loading: boolean): void {
        this.loadingSubject.next(loading);
    }

    /**
     * Define o campo/direção de ordenação **client-side** (aplicada em
     * {@link fetch}). O datatable chama `clearSorting().sortBy(...).fetch()`.
     */
    public sortBy(
        field: string,
        direction: 'asc' | 'desc' | undefined,
    ): ArrayDataSource<T> {
        this.sortField = direction ? field : null;
        this.sortDirection = direction;
        return this;
    }

    public clearSorting(): ArrayDataSource<T> {
        this.sortField = null;
        this.sortDirection = undefined;
        return this;
    }

    public fetch(): void {
        this.emit();
    }

    /** Reemite os dados aplicando a ordenação atual. */
    private emit(): void {
        this.pagedData.next(this.buildPage(this.sortedData()));
    }

    /** Dados ordenados conforme o estado atual (ordem original se não houver). */
    private sortedData(): T[] {
        if (!this.sortField || !this.sortDirection) {
            return this.originalData;
        }
        const field = this.sortField;
        const direction = this.sortDirection;
        return [...this.originalData].sort((a, b) => {
            const aVal = String((a as Record<string, unknown>)?.[field] ?? '');
            const bVal = String((b as Record<string, unknown>)?.[field] ?? '');
            const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
            return direction === 'asc' ? cmp : -cmp;
        });
    }

    private buildPage(data: T[]): Page<T> {
        return {
            data,
            pageSize: data.length,
            currentPage: 1,
            total: data.length,
        };
    }

    public goToPage(page: number): void {
        return;
    }

    /**
     * No-op: a fonte em array exibe todos os dados de uma vez, sem paginação
     * por servidor — não há tamanho de página a alterar.
     */
    public changePageSize(pageSize: number): void {
        return;
    }

    public onReady(): Observable<Page<T>> {
        return this.pagedData$;
    }

    isLoading(): Observable<boolean> {
        return this.loadingSubject.asObservable();
    }
}

export class PagedQueryDataSource<T> implements DataSource<T> {
    private readonly pagedQuerySignal: Signal<PagedQuery<T> | null>;

    constructor(pagedQuerySignal: Signal<PagedQuery<T> | null>) {
        this.pagedQuerySignal = pagedQuerySignal;
    }

    public sortBy(
        field: string,
        direction: 'asc' | 'desc' | undefined,
    ): PagedQueryDataSource<T> {
        this.pagedQuerySignal()!.sortBy(field, direction);
        return this;
    }

    public clearSorting(): PagedQueryDataSource<T> {
        this.pagedQuerySignal()!.clearSorting();
        return this;
    }

    public fetch(): void {
        this.pagedQuerySignal()!.fetch();
    }

    public goToPage(page: number): void {
        this.pagedQuerySignal()!.goToPage(page);
    }

    public changePageSize(pageSize: number): void {
        this.pagedQuerySignal()!.changePageSize(pageSize);
    }

    public onReady(): Observable<Page<T>> {
        return this.pagedQuerySignal()!.onReady();
    }

    public isLoading(): Observable<boolean> {
        return this.pagedQuerySignal()!.isLoading();
    }
}
